import React, { useCallback, useState } from 'react';
import { ConfirmModal } from '../components/sp-overlays';
import { PShell, SpWorkspaceLoading, useApp } from '../components/sp-shell';
import { useCreditorDetailModals } from '../hooks/use-creditor-detail-modals';
import { useCreditorFilters } from '../hooks/use-creditor-filters';
import { useCreditorListData } from '../hooks/use-creditor-list-data';
import { useCreditorPoll } from '../hooks/use-creditor-poll';
import { CreditorKpisSyncBar } from './list-creditores/creditor-kpis-sync-bar';
import { CreditorListCard } from './list-creditores/creditor-list-card';
import { CreditorListModals } from './list-creditores/creditor-list-modals';

export function PCompanyCreditors() {
  const app = useApp();
  const companyId = Number(app.params?.companyId);
  const company = app.companies.find((c) => c.id === companyId);
  const followSyncLogs = useCallback(() => {
    if (!companyId) return;
    app.navigate('company-sync-logs', { companyId });
  }, [app, companyId]);

  const {
    page,
    setPage,
    q,
    setQ,
    qDebounced,
    status,
    setStatus,
    supplier,
    setSupplier,
    employee,
    setEmployee,
  } = useCreditorFilters(companyId);

  const [confirmModal, setConfirmModal] = useState(null);
  const [creditorsListTick, setCreditorsListTick] = useState(0);
  const bumpCreditorsListTick = useCallback(() => {
    setCreditorsListTick((t) => t + 1);
  }, []);

  const {
    loading,
    err,
    payload,
    creditorKpis,
    creditorKpisLoading,
    creditorKpisErr,
  } = useCreditorListData({
    app,
    companyId,
    page,
    qDebounced,
    status,
    supplier,
    employee,
    creditorsListTick,
  });

  const {
    syncQueueActive,
    syncTotalModal,
    syncTotalRunning,
    runSyncTotal,
    closeSyncTotalModal,
  } = useCreditorPoll({
    app,
    companyId,
    creditorsListTick,
    setPage,
    bumpCreditorsListTick,
  });

  const {
    bankModal,
    paymentsModal,
    addressModal,
    contactModal,
    closeBankModal,
    closePaymentsModal,
    closeAddressModal,
    closeContactModal,
    openBankModal,
    openPaymentsModal,
    openAddressModal,
    openContactModal,
  } = useCreditorDetailModals({ app, companyId });

  const handleDeleteCreditor = useCallback(async (creditor) => {
    if (creditor.has_approved_payments) {
      app.toast('info', 'Exclusão não permitida', 'Este credor não pode ser excluído por possuir pagamentos salvos no sistema.');
      return;
    }
    setConfirmModal({
      title: 'Excluir credor',
      message: `Excluir o credor "${creditor.name}"? Esta ação removerá o credor e seus dados de endereço, contato e pagamentos sem aprovação.`,
      confirmLabel: 'Excluir',
      onConfirm: async () => {
        try {
          await app.deleteCreditor(companyId, creditor.id);
          app.toast('success', 'Credor excluído', `"${creditor.name}" foi removido.`);
          bumpCreditorsListTick();
          app.syncFromApi(undefined, 'batches');
        } catch (e) {
          app.toast('error', 'Falha ao excluir credor', e?.message || 'Verifique os dados e tente novamente.');
        }
      },
    });
  }, [app, companyId, bumpCreditorsListTick]);

  if (!app.workspaceReady) {
    return <SpWorkspaceLoading active="companies" title="A carregar credores…" />;
  }

  if (!companyId) {
    return (
      <PShell active="creditors">
        <div className="sp-screen-head">
          <h1 className="sp-screen-head__title">Empresa inválida</h1>
        </div>
        <button type="button" className="sp-btn sp-btn-outline" onClick={() => app.navigate('companies')}>
          Voltar às empresas
        </button>
      </PShell>
    );
  }

  if ((loading && !payload) || (creditorKpisLoading && !creditorKpis)) {
    return <SpWorkspaceLoading active="creditors" title="A carregar credores…" />;
  }

  return (
    <PShell active="creditors">
      <div className="sp-screen-head">
        <div>
          <div className="sp-caps sp-screen-head__caps">Credores</div>
          <h1 className="sp-screen-head__title">{company?.name || `Empresa #${companyId}`}</h1>
        </div>
      </div>

      <CreditorKpisSyncBar
        creditorKpisErr={creditorKpisErr}
        creditorKpis={creditorKpis}
        creditorKpisLoading={creditorKpisLoading}
        syncQueueActive={syncQueueActive}
        syncTotalModalOpen={syncTotalModal.open}
        syncTotalRunning={syncTotalRunning}
        onSyncTotal={runSyncTotal}
      />

      <CreditorListCard
        q={q}
        setQ={setQ}
        status={status}
        setStatus={setStatus}
        supplier={supplier}
        setSupplier={setSupplier}
        employee={employee}
        setEmployee={setEmployee}
        loading={loading}
        err={err}
        payload={payload}
        page={page}
        setPage={setPage}
        onOpenBank={openBankModal}
        onOpenPayments={openPaymentsModal}
        onOpenAddress={openAddressModal}
        onOpenContact={openContactModal}
        onDelete={handleDeleteCreditor}
        isAdmin={Array.isArray(app.currentUser?.permissions) && app.currentUser.permissions.includes('Administrador')}
      />

      <CreditorListModals
        syncTotalModal={syncTotalModal}
        onCloseSyncTotal={closeSyncTotalModal}
        onFollowSyncLogs={followSyncLogs}
        bankModal={bankModal}
        onCloseBank={closeBankModal}
        paymentsModal={paymentsModal}
        onClosePayments={closePaymentsModal}
        addressModal={addressModal}
        onCloseAddress={closeAddressModal}
        contactModal={contactModal}
        onCloseContact={closeContactModal}
      />
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        onConfirm={() => {
          confirmModal?.onConfirm?.();
          setConfirmModal(null);
        }}
        onCancel={() => setConfirmModal(null)}
      />
    </PShell>
  );
}
